import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App as CapApp } from '@capacitor/app';
import { supabase } from '../lib/supabase';
import { CURRENT_TERMS_VERSION } from '../lib/terms';
import { deleteOwnAccount, DELETE_CONFIRM_TEXT } from '../lib/deleteAccount';

const AuthCtx = createContext(null);

// LEGAL-04: one-click "Terms changed" interstitial. Shown whenever the logged-in user's stored
// terms_version is missing (legacy account, predates LEGAL-01) or older than the current one
// (Terms.jsx/Privacy.jsx changed since they last agreed). Not a dead end — one button re-records
// consent and it never shows again until the version bumps.
// Nesúhlasím → odhlásiť sa alebo zmazať účet (ten istý krok ako v Nastaveniach, lib/deleteAccount).
function TermsUpdateModal({ onAcknowledge, onSignOut }) {
  const [step, setStep] = useState('consent');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const goTo = (nextStep) => {
    setErr('');
    setBusy(false);
    setStep(nextStep);
  };

  const handleAcknowledge = async () => {
    setBusy(true);
    setErr('');
    try {
      await onAcknowledge();
    } catch (e) {
      setErr(e?.message || 'Nepodarilo sa uložiť súhlas, skús to prosím znova.');
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    setBusy(true);
    setErr('');
    try {
      await onSignOut();
      window.location.href = '/login';
    } catch (e) {
      setErr(e?.message || 'Nepodarilo sa odhlásiť, skús to prosím znova.');
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setErr('');
    try {
      await deleteOwnAccount();
    } catch (e) {
      setErr(`Zmazanie zlyhalo: ${String(e?.message || 'neznáma chyba').replace(/\.?$/, '.')} Skús znova alebo napíš na support@todue.sk.`);
      setBusy(false);
      return;
    }
    // Účet je už zmazaný — zlyhané odhlásenie nesmie vyzerať ako zlyhané zmazanie ani povoliť opakovanie.
    try { await onSignOut(); } catch { /* relácia patrí zmazanému účtu, aj tak neplatí */ }
    window.location.href = '/login';
  };

  const titles = {
    consent: 'Podmienky sa zmenili',
    decline: 'Bez súhlasu nemôžeš pokračovať',
    delete: 'Zmazať účet natrvalo?',
  };

  return (
    <div
      data-testid="terms-update-modal"
      role="dialog"
      aria-modal="true"
      aria-label={titles[step]}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div style={{
        width: '100%', maxWidth: 420,
        background: 'var(--bg-surface, #fff)',
        borderRadius: 12,
        padding: 24,
        boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
      }}>
        {step === 'consent' && (
          <>
            <h2 style={{ margin: '0 0 10px', fontSize: 'var(--t-lg)', fontWeight: 700 }}>Podmienky sa zmenili</h2>
            <p style={{ margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.5 }}>
              Aktualizovali sme Podmienky používania a/alebo Zásady ochrany osobných údajov.
              Pokračovaním potvrdzuješ, že s nimi súhlasíš.
            </p>
            <p style={{ margin: '0 0 16px', fontSize: 12.5 }}>
              <a href="/terms" target="_blank" rel="noopener noreferrer">Podmienky</a>
              {' · '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Zásady ochrany osobných údajov</a>
            </p>
            {err && <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--danger)' }}>{err}</p>}
            <button
              type="button"
              data-testid="terms-update-ack"
              className="cd-btn cd-btn-primary"
              onClick={handleAcknowledge}
              disabled={busy}
              style={{ width: '100%' }}
            >
              {busy ? 'Ukladám…' : 'Rozumiem, súhlasím'}
            </button>
            <button
              type="button"
              data-testid="terms-decline"
              className="cd-btn cd-btn-ghost"
              onClick={() => goTo('decline')}
              disabled={busy}
              style={{ width: '100%', marginTop: 8 }}
            >
              Nesúhlasím
            </button>
          </>
        )}

        {step === 'decline' && (
          <>
            <h2 style={{ margin: '0 0 10px', fontSize: 'var(--t-lg)', fontWeight: 700 }}>Bez súhlasu nemôžeš pokračovať</h2>
            <p style={{ margin: '0 0 16px', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              Podmienky a zásady ochrany osobných údajov sú pravidlá, za ktorých todue prevádzkujeme. Bez súhlasu s nimi appku nemôžeš používať. Môžeš sa odhlásiť a rozhodnúť sa neskôr, alebo si účet zmazať.
            </p>
            {err && <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--danger)' }}>{err}</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                data-testid="terms-decline-signout"
                className="cd-btn cd-btn-secondary"
                onClick={handleSignOut}
                disabled={busy}
                style={{ width: '100%' }}
              >
                {busy ? 'Odhlasujem…' : 'Odhlásiť sa'}
              </button>
              <button
                type="button"
                data-testid="terms-decline-delete"
                className="cd-btn cd-btn-danger"
                onClick={() => goTo('delete')}
                disabled={busy}
                style={{ width: '100%' }}
              >
                Zmazať účet
              </button>
              <button
                type="button"
                data-testid="terms-decline-back"
                className="cd-btn cd-btn-ghost"
                onClick={() => goTo('consent')}
                disabled={busy}
                style={{ width: '100%' }}
              >
                Späť na podmienky
              </button>
            </div>
          </>
        )}

        {step === 'delete' && (
          <>
            <h2 style={{ margin: '0 0 10px', fontSize: 'var(--t-lg)', fontWeight: 700 }}>Zmazať účet natrvalo?</h2>
            <p style={{ margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              Zmažeme tvoj účet a údaje na našom serveri (profil, klientov, faktúry, výdavky, nahrané doklady) a odvoláme prístup ku Google. Dáta v tomto zariadení a súbory v tvojom Google Drive ostávajú tebe. Obnovenie nie je možné.
            </p>
            <p style={{ margin: '0 0 16px', fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
              Doklady musíš ako podnikateľ uchovať 10 rokov. Ak potrebuješ ich export, napíš na support@todue.sk ešte pred zmazaním.
            </p>
            <label htmlFor="terms-delete-confirm" style={{ display: 'block', margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-secondary)' }}>
              Pre potvrdenie napíš {DELETE_CONFIRM_TEXT}
            </label>
            <input
              id="terms-delete-confirm"
              type="text"
              className="cd-input"
              data-testid="terms-delete-input"
              autoComplete="off"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={busy}
              style={{ width: '100%', marginBottom: 16 }}
            />
            {err && <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--danger)' }}>{err}</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                data-testid="terms-delete-confirm"
                className="cd-btn cd-btn-danger"
                onClick={handleDelete}
                disabled={busy || confirmText.trim().toUpperCase() !== DELETE_CONFIRM_TEXT}
                style={{ width: '100%' }}
              >
                {busy ? 'Mažem…' : 'Zmazať natrvalo'}
              </button>
              <button
                type="button"
                data-testid="terms-delete-back"
                className="cd-btn cd-btn-ghost"
                onClick={() => { setConfirmText(''); goTo('decline'); }}
                disabled={busy}
                style={{ width: '100%' }}
              >
                Späť
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const GOOGLE_BASE_SCOPES = 'openid email profile';

// Na natívnej (Capacitor) platforme OAuth/magic-link redirectuje na custom scheme
// deep link, ktorý znovu otvorí appku — namiesto https://localhost (čo by ostalo
// v systémovom prehliadači). Na webe ostáva pôvodné správanie.
const IS_NATIVE = Capacitor.isNativePlatform();
const NATIVE_REDIRECT = 'sk.todue.app://auth/callback';
const authRedirect = () =>
  IS_NATIVE ? NATIVE_REDIRECT : window.location.origin + '/auth/callback';

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // undefined = not checked yet (don't flash the modal while we don't know), null = legacy
  // account with no consent on file, string = version last accepted.
  const [termsVersion, setTermsVersion] = useState(undefined);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      // Supabase fíruje onAuthStateChange (SIGNED_IN) pri každom návrate fokusu na
      // tab/okno. Naivné setSession(s) dá novú referenciu session → re-render celej
      // appky a refetch dát (WorkspaceContext.fetch závisí na `user`). Aktualizuj len
      // pri reálnej zmene prihlásenia (token alebo user), inak nechaj pôvodnú referenciu.
      setSession((prev) => {
        if (prev?.access_token === s?.access_token && prev?.user?.id === s?.user?.id) {
          return prev;
        }
        return s;
      });
    });

    // Natívny deep-link návrat z OAuth/magic-link: appka sa znovu otvorí cez
    // sk.todue.app://auth/callback?code=... — dokončíme prihlásenie a zavrieme browser.
    const handleAuthDeepLink = async (url) => {
      if (!url || !url.includes('auth/callback')) return;
      // Drive connect návrat (nie login) — token už uložil callback, len zavrieme okno.
      // O zvyšok (toast, resolve) sa stará polling v requestDriveAccess.
      if (url.includes('drive_connected')) {
        try { await Browser.close(); } catch { /* okno už zatvorené */ }
        return;
      }
      try {
        const parsed = new URL(url);
        const code = parsed.searchParams.get('code');
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) console.error('[deeplink] exchangeCodeForSession:', error.message);
        } else if (url.includes('access_token')) {
          const hp = new URLSearchParams((url.split('#')[1] || ''));
          const at = hp.get('access_token');
          const rt = hp.get('refresh_token');
          if (at && rt) await supabase.auth.setSession({ access_token: at, refresh_token: rt });
        }
      } catch (e) {
        console.error('[deeplink] handling failed:', e);
      } finally {
        try { await Browser.close(); } catch { /* browser už zatvorený */ }
      }
    };

    let urlListener;
    if (IS_NATIVE) {
      // Warm: appka beží, deep link príde ako event.
      urlListener = CapApp.addListener('appUrlOpen', ({ url }) => handleAuthDeepLink(url));
      // Cold start: appku spustil deep link skôr než sa listener stihol zaregistrovať
      // — preto skontrolujeme launch URL aj pri inicializácii.
      CapApp.getLaunchUrl().then((res) => { if (res?.url) handleAuthDeepLink(res.url); }).catch(() => {});
    }

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe?.();
      if (urlListener) urlListener.then((l) => l.remove()).catch(() => {});
    };
  }, []);

  // LEGAL-04: whenever the logged-in user changes, check whether their stored terms_version
  // is missing or stale. Covers every entry point that doesn't run through Login.jsx's
  // checkbox — Google OAuth signups, and any legacy account that predates LEGAL-01.
  const userId = session?.user?.id;
  useEffect(() => {
    if (!userId) {
      setTermsVersion(undefined);
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    let attempt = 0;
    // Unknown consent state (read error / offline) keeps termsVersion undefined → no modal, but we
    // retry, otherwise a never-consented user would skip the modal for the whole session
    // (cross-family review 2026-10-03). Backoff 3s, 6s, 12s… capped at 60s.
    const retry = () => {
      if (cancelled) return;
      setTermsVersion(undefined);
      timer = setTimeout(load, Math.min(3000 * 2 ** attempt, 60000));
      attempt += 1;
    };
    const load = () => {
      supabase
        .from('users_profile')
        .select('terms_version')
        .eq('id', userId)
        .maybeSingle()
        .then(({ data, error }) => {
          if (cancelled) return;
          if (error) return retry();
          // No row or terms_version null → never consented → modal.
          setTermsVersion(data?.terms_version ?? null);
        })
        .catch(retry);
    };
    load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [userId]);

  const currentUserIdRef = useRef(userId);
  currentUserIdRef.current = userId;
  const acknowledgeTerms = useCallback(async () => {
    if (!userId) return;
    // Use upsert to handle the case where the users_profile row is missing entirely
    // (e.g. legacy account created before the profile row was guaranteed). Previously
    // .update() silently did nothing for such users, causing the consent to be lost
    // and the modal to reappear on every restart.
    const { error } = await supabase
      .from('users_profile')
      .upsert(
        {
          id: userId,
          terms_accepted_at: new Date().toISOString(),
          terms_version: CURRENT_TERMS_VERSION,
        },
        { onConflict: 'id' }
      );
    if (error) throw error;
    // Account switched while saving → the new account's own read decides, not this ack.
    if (currentUserIdRef.current === userId) setTermsVersion(CURRENT_TERMS_VERSION);
  }, [userId]);

  const needsTermsAck = !!userId && termsVersion !== undefined && termsVersion !== CURRENT_TERMS_VERSION;

  // Google-only auth (2026-09-27): magic-link (signInWithOtp) + email/password sign-up/sign-in/
  // reset were removed — the product requires a Google account for core functionality (Drive is
  // the backup/sync backend), so a non-Google signup path created users who could never actually
  // use the product. signInWithGoogle is now the only entry point; its consent capture goes
  // through the terms_version staleness check below (unchanged — see needsTermsAck), not through
  // options.data metadata the way the removed password/magic-link paths used to.
  const signInWithGoogle = useCallback(async () => {
    const redirectTo = authRedirect();
    const options = {
      redirectTo,
      scopes: `${GOOGLE_BASE_SCOPES} ${GOOGLE_DRIVE_SCOPE}`,
      queryParams: { access_type: 'offline', prompt: 'consent' },
    };
    if (IS_NATIVE) {
      // Na mobile otvoríme OAuth v in-app prehliadači a vraciame sa cez deep link.
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { ...options, skipBrowserRedirect: true },
      });
      if (error) throw error;
      if (data?.url) await Browser.open({ url: data.url, presentationStyle: 'popover' });
      return;
    }
    const { error } = await supabase.auth.signInWithOAuth({ provider: 'google', options });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    let failed = false;
    try { failed = !!((await supabase.auth.signOut()) || {}).error; } catch { failed = true; }
    // Server sign-out failed (offline…) → the session would stay in storage; drop it locally anyway,
    // and never leave the user stuck on the screen (cross-family review 2026-10-03).
    if (failed) { try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* still leave */ } }
    setSession(null);
    // V2 screens are a public layout (no auth gate), so nothing reacts to the cleared session and the
    // button looked dead (beta 2026-10-03). Go to this origin's login page — beta stays on beta.
    window.location.assign('/login');
  }, []);

  // Phase 1 of GDRIVE-MIGRATION-PLAN — separate OAuth flow for Drive scope.
  // Uses DB polling instead of postMessage because Chrome's Cross-Origin-Opener-Policy
  // blocks postMessage from supabase.co popup back to localhost/app.todue.sk opener.
  // Polling user_google_tokens for the row inserted by oauth-drive-callback is robust
  // across COOP and COEP, and handles the race window where popup closes before message dispatches.
  const requestDriveAccess = useCallback(async () => {
    if (!session?.user?.id) throw new Error('Not signed in');
    const userId = session.user.id;

    // Natívna appka (Capacitor): Drive OAuth v in-app Browseri, návrat cez deep link
    // (sk.todue.app://auth/callback?drive_connected=1) — rovnaký princíp ako login.
    // Promise sa resolvne pollovaním has_drive_access(), keď callback uloží token.
    if (IS_NATIVE) {
      const { data, error } = await supabase.functions.invoke('drive-oauth-start', {
        body: { return_url: NATIVE_REDIRECT },
      });
      if (error) throw new Error(`drive-oauth-start failed: ${error.message}`);
      if (!data?.auth_url) throw new Error('drive-oauth-start returned no auth_url');
      await Browser.open({ url: data.auth_url, presentationStyle: 'popover' });
      return new Promise((resolve, reject) => {
        let tries = 0;
        const iv = setInterval(async () => {
          tries += 1;
          try {
            const { data: connected } = await supabase.rpc('has_drive_access');
            if (connected === true) {
              clearInterval(iv);
              try { await Browser.close(); } catch (_) { /* okno už zatvorené */ }
              resolve(true);
              return;
            }
          } catch (_) { /* transient — pokračuj v pollovaní */ }
          if (tries > 120) { clearInterval(iv); reject(new Error('Drive connect timeout')); }
        }, 1500);
      });
    }

    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const returnUrl = isMobile ? window.location.href : null;

    const { data, error } = await supabase.functions.invoke('drive-oauth-start', {
      body: returnUrl ? { return_url: returnUrl } : undefined,
    });
    if (error) throw new Error(`drive-oauth-start failed: ${error.message}`);
    if (!data?.auth_url) throw new Error('drive-oauth-start returned no auth_url');

    if (isMobile) {
      sessionStorage.setItem('drive_oauth_return', returnUrl);
      window.location.href = data.auth_url;
      return new Promise(() => { /* navigates away — never resolves */ });
    }

    const popup = window.open(
      data.auth_url,
      'drive-oauth',
      'width=520,height=720,popup=yes',
    );
    if (!popup) throw new Error('Popup blocked. Allow popups for tento web.');

    return new Promise((resolve, reject) => {
      let settled = false;
      const cleanup = () => {
        clearInterval(pollTokenInterval);
        clearInterval(closedPoll);
        clearTimeout(timeoutHandle);
      };
      const finish = (fn, val) => {
        if (settled) return;
        settled = true;
        cleanup();
        fn(val);
      };

      // Poll DB via has_drive_access() RPC. Direct SELECT on user_google_tokens
      // is RLS-blocked for users (refresh_token must not be readable client-side),
      // so we use a SECURITY DEFINER function that returns just a boolean.
      const pollTokenInterval = setInterval(async () => {
        try {
          const { data: connected } = await supabase.rpc('has_drive_access');
          if (connected === true) {
            try { popup.close(); } catch (_) { /* COOP may block, ignore */ }
            finish(resolve, true);
          }
        } catch (_) {
          // Transient errors — keep polling until timeout
        }
      }, 1500);

      // Detect popup close. Wrapped in try/catch because Cross-Origin-Opener-Policy
      // can throw when reading `popup.closed` from a cross-origin window. Grace
      // period of 3s after detected-closed for DB write to be visible.
      let popupClosedAt = null;
      const closedPoll = setInterval(() => {
        if (settled) return;
        let closed = false;
        try { closed = popup.closed; } catch (_) { /* COOP block — assume open */ }
        if (closed) {
          if (!popupClosedAt) popupClosedAt = Date.now();
          else if (Date.now() - popupClosedAt > 3000) {
            finish(reject, new Error('Pripojenie zrušené — popup zatvorený pred dokončením.'));
          }
        }
      }, 500);

      const timeoutHandle = setTimeout(() => {
        try { popup.close(); } catch (_) { /* ignore */ }
        finish(reject, new Error('Drive connect timeout'));
      }, 180_000);
    });
  }, [session]);

  const purgeDriveCredentials = useCallback(async () => {
    const { error } = await supabase.rpc('purge_drive_credentials');
    if (error) throw error;
  }, []);

  const value = {
    session,
    user: session?.user || null,
    loading,
    signInWithGoogle,
    signOut,
    requestDriveAccess,
    purgeDriveCredentials,
    acknowledgeTerms,
    needsTermsAck,
  };

  return (
    <AuthCtx.Provider value={value}>
      {children}
      {needsTermsAck && <TermsUpdateModal onAcknowledge={acknowledgeTerms} onSignOut={signOut} />}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
